/**
 * DatasetList — список датасетов проекта.
 *
 * - Auto-refresh когда есть status in (uploaded, parsing).
 * - StatusBadge с цветом по статусу.
 * - Действия: Open / Transform / Reparse / Delete.
 * - Delete идёт через <ConfirmDialog> (НЕ window.confirm) — на быстром
 *   двойном клике / iframe / tab blur window.confirm ненадёжен и приводил к
 *   удалению без диалога (регрессия 2026-08-06, data-loss risk).
 *   В диалоге — реальное имя файла и (если это активный датасет проекта)
 *   предупреждение, что project.active_dataset_id сбросится.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Trash2, RotateCw, ArrowRight, Loader2 } from 'lucide-react';

import { useDeleteDataset, useReparseDataset, type Dataset } from '@/hooks/useFiles';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { FileTypeIcon } from '@/components/common/FileTypeIcon';
import { useProjectStore } from '@/store/projectStore';
import { cn } from '@/lib/utils';

interface DatasetListProps {
  datasets: Dataset[];
  isLoading?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  uploaded: 'bg-surface-700 text-surface-300',
  parsing: 'bg-info-tint/20 text-info300',
  ready: 'bg-success-tint/20 text-success',
  failed: 'bg-danger-tint/20 text-danger',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function DatasetList({ datasets, isLoading }: DatasetListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteMutation = useDeleteDataset();
  const reparseMutation = useReparseDataset();
  const activeDatasetId = useProjectStore((s) => s.activeDatasetId);

  // Pending delete — {dataset, isActive}, либо null когда диалог закрыт.
  // Держим в state, чтобы диалог получал реальное имя + busy-флаг мутации.
  const [pendingDelete, setPendingDelete] = useState<Dataset | null>(null);

  if (isLoading) {
    return (
      <div className="text-sm text-surface-400">{t('common.loading')}</div>
    );
  }
  if (datasets.length === 0) {
    return (
      <div className="card text-sm text-surface-400 text-center py-8">
        {t('files.list.empty')}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {datasets.map((d) => {
          const isBusy = d.status === 'uploaded' || d.status === 'parsing';
          return (
            <div
              key={d.id}
              className="card flex items-center gap-4 py-3"
            >
              <FileTypeIcon fileType={d.file_type} className="w-5 h-5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{d.name}</span>
                  <span
                    className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider',
                      STATUS_COLORS[d.status] || STATUS_COLORS.uploaded,
                    )}
                  >
                    {t(`files.status.${d.status}`)}
                  </span>
                  {d.id === activeDatasetId && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-success-tint/15 text-success border border-success-tint/30 uppercase tracking-wider"
                      title={t('files.status.active_hint', {
                        defaultValue: 'Активный датасет проекта',
                      })}
                    >
                      {t('files.status.active', { defaultValue: 'В работе' })}
                    </span>
                  )}
                </div>
                <div className="text-xs text-surface-500 mt-0.5 flex gap-3 flex-wrap">
                  <span>{d.original_filename}</span>
                  <span>·</span>
                  <span>{formatBytes(d.file_size_bytes)}</span>
                  <span>·</span>
                  <span>{d.total_rows} rows · {d.total_columns} cols</span>
                  <span>·</span>
                  <span>{formatDate(d.created_at)}</span>
                </div>
                {d.status === 'failed' && d.error_message && (
                  <div
                    className="text-xs text-danger mt-1 px-2 py-1 rounded bg-danger-tint/10 border border-danger-tint/20 whitespace-normal break-words max-w-2xl"
                    data-testid="dataset-list-failed-inline"
                  >
                    <span className="font-semibold">
                      {t('files.list.failed_inline', {
                        defaultValue: 'Помилка:',
                      })}
                    </span>{' '}
                    {d.error_message}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate(`/files/results?id=${d.id}`)}
                  className="btn-ghost text-xs"
                  title={t('files.list.open')}
                >
                  {t('files.list.open')}
                </button>
                <button
                  onClick={() => navigate(`/files/transform?id=${d.id}`)}
                  className="btn-ghost text-xs"
                  title={t('files.list.go_transform')}
                >
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
                {d.status === 'failed' && (
                  <button
                    onClick={() => reparseMutation.mutate(d.id)}
                    className="btn-ghost text-xs"
                    disabled={isBusy || reparseMutation.isPending}
                    aria-label={t('files.list.retry', {
                      defaultValue: 'Повторити',
                    })}
                    title={t('files.list.retry', {
                      defaultValue: 'Повторити',
                    })}
                    data-testid="dataset-list-retry"
                  >
                    {reparseMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RotateCw className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => setPendingDelete(d)}
                  className="btn-ghost text-xs text-danger"
                  disabled={deleteMutation.isPending}
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('files.list.delete_confirm_title', {
          defaultValue: 'Удалить файл',
        })}
        message={
          pendingDelete?.id === activeDatasetId
            ? t('files.list.delete_confirm_active', {
                name: pendingDelete.name || t('files.list.delete_fallback', {
                  defaultValue: 'этот файл',
                }),
              })
            : t('files.list.delete_confirm', {
                name: pendingDelete?.name || t('files.list.delete_fallback', {
                  defaultValue: 'этот файл',
                }),
              })
        }
        confirmLabel={t('common.delete', { defaultValue: 'Удалить' })}
        cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
        confirmVariant="danger"
        busy={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            const id = pendingDelete.id;
            deleteMutation.mutate(id);
            setPendingDelete(null);
          }
        }}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDelete(null);
        }}
      />
    </>
  );
}
